/**
 * NewConversationPage — /conversations/new. Starts a conversation from its first message on the `FormPage` template
 * (NextAdmin `profile/account/page.tsx`; demo `/form-layouts`): the message in a validated form, the assistant context
 * (scope, runtime policy, recommended starts) in the aside.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { Suspense, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Can } from '../../components/auth/Can.js';
import { Form, FormActions, TextAreaFormField, useZodForm } from '../../components/form/index.js';
import { FormPage } from '../../components/page/FormPage.js';
import { Button, Callout, Card } from '../../components/ui/index.js';
import { RuntimePolicyPicker } from '../../components/ui/RuntimePolicyPicker.js';
import {
  getConversationQueryKey,
  startConversationFromMessageMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { seedConversationMessages } from '../../hooks/useConversationStream.js';
import { RuntimePolicySurface } from '../../generated/api/types.gen.js';
import { zConversationQuickStart } from '../../generated/api/zod.gen.js';
import { useActiveModule } from '../../hooks/useActiveModule.js';
import { useProject } from '../../hooks/useProjects.js';
import { useIsModuleEnabled } from '../../modules/useEnabledModules.js';
import { subjectLabel } from './conversations-list/conversation-labels.js';
import {
  type IntentPreset,
  buildGenericPresets,
  buildProjectPresets,
  buildStartRequest,
} from './new-conversation-start.js';

export type { IntentPreset } from './new-conversation-start.js';

// QG-CLI-PAGES-005: the finance persona chips are loaded from the finance module only when it is enabled, via a lazy
// import — the finance-specific fetch hook must not be statically imported here (that would ship it in every org's
// core bundle). See `modules/finance/client/pages/FinanceConversationPresets.tsx`.
const FinanceConversationPresetsLoader = React.lazy(() =>
  import('../../../../modules/finance/client/pages/FinanceConversationPresets.js').then((m) => ({
    default: m.FinanceConversationPresetsLoader,
  })),
);

const startSchema = zConversationQuickStart.pick({ message: true });

export const NewConversationPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  // null = the kind-of-work default, preselected in the picker and left to the server to resolve.
  const [runtimePolicyId, setRuntimePolicyId] = useState<number | null>(null);
  const isFinanceEnabled = useIsModuleEnabled('finance');
  const { activeModuleId } = useActiveModule();
  const start = useMutation(startConversationFromMessageMutation());

  const context = {
    projectId: searchParams.get('projectId'),
    teamId: searchParams.get('teamId'),
    parentConversationId: searchParams.get('parentConversationId'),
    subjectType: searchParams.get('subjectType'),
    subjectId: searchParams.get('subjectId'),
  };
  const form = useZodForm(startSchema, { defaultValues: { message: searchParams.get('message') ?? '' } });
  const message = form.watch('message');

  const { data: project } = useProject(context.projectId ?? undefined);
  const projectName = project?.name ?? (context.projectId ? 'this project' : 'the project');
  // Finance persona chips render from the listing API (single source of truth), fetched only when the module is
  // enabled and we are not in a project scope.
  const [financePresets, setFinancePresets] = useState<IntentPreset[]>([]);
  const presets = useMemo<IntentPreset[]>(() => {
    if (context.projectId) return buildProjectPresets(projectName);
    const base = buildGenericPresets();
    return isFinanceEnabled === true ? [...base, ...financePresets] : base;
  }, [context.projectId, projectName, isFinanceEnabled, financePresets]);
  // A preset pins its persona only while its text is untouched.
  const selectedPreset = presets.find((preset) => preset.message === message);

  const backTo = context.projectId ? `/projects/${context.projectId}?tab=conversations` : '/conversations';
  const breadcrumbs = context.projectId
    ? [
        { label: 'Projects', to: '/projects' },
        { label: project?.name ?? 'Project', to: `/projects/${context.projectId}` },
        { label: 'Conversations', to: backTo },
      ]
    : [
        { label: 'AI Agents', to: '/agents/libraries' },
        { label: 'Conversations', to: '/conversations' },
      ];

  const begin = async (values: { message: string }) => {
    const data = await start.mutateAsync({
      body: buildStartRequest({
        message: values.message,
        moduleId: activeModuleId,
        context,
        templateSlug: selectedPreset?.templateSlug,
        runtimePolicyId,
      }),
    });
    // Seed the caches the conversation page reads, so it opens on the first message instead of a blank thread.
    const conversationId = String(data.conversation.id);
    queryClient.setQueryData(getConversationQueryKey({ path: { id: conversationId } }), data.conversation);
    seedConversationMessages(queryClient, conversationId, [data.message]);
    navigate(
      context.projectId
        ? `/conversations/${data.conversation.id}?projectId=${context.projectId}`
        : `/conversations/${data.conversation.id}`,
      {
        state: {
          projectBreadcrumb: context.projectId
            ? { projectId: context.projectId, projectName: project?.name ?? null }
            : null,
          firstMessageId: data.message.id,
        },
      },
    );
  };

  const aside = (
    <div className="space-y-4">
      <Card title="Assistant context">
        <dl className="space-y-1 text-sm">
          <dt className="text-muted">Scope</dt>
          <dd className="font-medium text-heading">{context.projectId ? projectName : 'General'}</dd>
          {context.subjectType && (
            <dd className="text-muted">
              {subjectLabel(context.subjectType)}
              {context.subjectId ? ` ${context.subjectId}` : ''}
            </dd>
          )}
          {context.parentConversationId && <dd className="text-muted">Continues an earlier conversation</dd>}
        </dl>
        <p className="mt-3 text-xs text-muted">
          Drafts are proposed for review before Conflui creates or dispatches work.
        </p>
      </Card>
      <Card title="Runtime policy">
        <RuntimePolicyPicker
          surface={RuntimePolicySurface.CONVERSATION}
          value={runtimePolicyId}
          onChange={setRuntimePolicyId}
          disabled={form.formState.isSubmitting}
        />
      </Card>
      <Card title="Recommended starts">
        <div className="flex flex-col gap-2">
          {presets.map((preset) => {
            const Icon = preset.icon;
            const active = selectedPreset?.id === preset.id;
            return (
              <Button
                key={preset.id}
                variant={active ? 'primary' : 'secondary'}
                aria-pressed={active}
                onClick={() => form.setValue('message', preset.message, { shouldDirty: true, shouldValidate: true })}
                className="h-auto flex-col items-start justify-start gap-1 whitespace-normal px-3 py-2 text-left"
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Icon size={14} aria-hidden />
                  {preset.label}
                </span>
                <span className="text-xs font-normal opacity-80">{preset.description}</span>
              </Button>
            );
          })}
        </div>
      </Card>
    </div>
  );

  return (
    <FormPage
      title={mode === 'project-draft' ? 'Draft a project with the assistant' : 'New conversation'}
      subtitle={
        mode === 'project-draft'
          ? 'Start from a product or business idea. The assistant can propose a project draft and create it through the interaction protocol.'
          : context.projectId
            ? 'Start a project-scoped assistant thread. Presets keep the conversation tied to changes, tasks, workflows, workforce and durable outputs.'
            : 'Start with a message. Conflui chooses the conversation defaults and routes the work from there.'
      }
      breadcrumbs={breadcrumbs}
      backTo={backTo}
      aside={aside}
    >
      <Form
        form={form}
        onSubmit={begin}
        guardUnsaved
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            event.currentTarget.requestSubmit();
          }
        }}
      >
        <TextAreaFormField
          name="message"
          label="What do you want to work on?"
          placeholder="Describe the work, the question or the idea."
          description="Press Ctrl+Enter or Cmd+Enter to start."
          rows={12}
          required
        />
        <Can
          policies={['CanManageAgents', 'CanConverse']}
          mode="any"
          fallback={<Callout tone="warning">You can read conversations but not start one.</Callout>}
        >
          <FormActions submitLabel="Start conversation" cancelTo={backTo} />
        </Can>
      </Form>
      {isFinanceEnabled === true && (
        <Suspense fallback={null}>
          <FinanceConversationPresetsLoader enabled={!context.projectId} onPresets={setFinancePresets} />
        </Suspense>
      )}
    </FormPage>
  );
};
